import { type FetchResult } from "@apollo/client";

/**
 * Throws when an Apollo mutation result carries GraphQL errors.
 *
 * The internal-tools Apollo client is configured with `errorPolicy: 'all'`,
 * so a failed mutation does NOT reject — the errors are returned on
 * `result.errors` and `result.data` is null/partial. Without this guard a
 * caller that only inspects `result.data` silently treats a failure as a
 * no-op (e.g. the CSV import dialog shows neither a preview nor an error).
 * Calling this immediately after `await`-ing a mutation restores the
 * throw-on-error contract the dialog `try/catch` blocks were written for.
 *
 * @param result The resolved Apollo mutation result.
 * @throws Error joining every GraphQL error message when `result.errors` is
 * non-empty.
 */
export const throwOnGraphqlErrors = (
  result: Pick<FetchResult<unknown>, "errors">,
): void => {
  const { errors } = result;
  if (errors !== undefined && errors.length > 0) {
    throw new Error(errors.map((error) => error.message).join("; "));
  }
};
